import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { ChainingMethod } from '@/api/programBank';
import { useTrialResultCodes, type TrialResultCode } from '@/api/trialResultCodes';
import type { EntryWidgetProps } from './types';

/*
 * Task-analysis stepper — per-step scoring for an ordered step list.
 *
 * Each step picks from the same practice-configured result codes that
 * drive trial data (I, V, G, PP, FP, NR by default). Unscored steps are
 * skipped; `valueNumeric` = correct / scored * 100 (server recomputes on
 * save from each code's is_correct flag).
 *
 * valueJson.stepResults is [{ index, code }, …] — only entries for steps
 * the clinician actually scored. Step order and step definitions are
 * owned by the target (ta_steps); this widget references step rows by
 * their index.
 *
 * Chaining method (forward / backward / total-task) is surfaced as
 * context only. It governs workflow (which steps to teach in a session)
 * but does not change score math.
 */
interface TaStep {
    label?: string;
    [key: string]: unknown;
}

interface TaskAnalysisStepperProps extends EntryWidgetProps {
    steps: TaStep[];
    chainingMethod: ChainingMethod | null;
}

export function TaskAnalysisStepper({ value, onChange, disabled, steps, chainingMethod }: TaskAnalysisStepperProps) {
    const codesQuery = useTrialResultCodes();
    const codes = codesQuery.data ?? [];
    const activeCodes = codes.filter((c) => !c.archivedAt);
    const codeByValue = new Map(codes.map((c) => [c.code, c]));

    const scoredByIndex = new Map<number, string>();
    if (value.valueJson && 'stepResults' in value.valueJson) {
        for (const r of value.valueJson.stepResults as Array<{ index: number; code: string }>) {
            if (typeof r?.code === 'string' && r.code) scoredByIndex.set(r.index, r.code);
        }
    }

    const setStep = (i: number, code: string | null) => {
        if (disabled) return;
        const next = new Map(scoredByIndex);
        if (code === null) next.delete(i);
        else next.set(i, code);

        const entries = Array.from(next.entries()).sort(([a], [b]) => a - b);
        const correct = entries.filter(([, c]) => codeByValue.get(c)?.isCorrect).length;
        const pct = entries.length > 0 ? Math.round((correct / entries.length) * 100) : null;

        onChange({
            ...value,
            valueNumeric: pct,
            valueJson: { stepResults: entries.map(([index, c]) => ({ index, code: c })) },
            dirty: true,
        });
    };

    if (codesQuery.isLoading) {
        return <div className="flex items-center gap-2 text-xs text-text-subtle"><Loader2 className="w-3 h-3 animate-spin" /> Loading result codes…</div>;
    }
    if (codesQuery.isError) {
        return <div className="text-xs text-error">Couldn't load result codes.</div>;
    }
    if (activeCodes.length === 0) {
        return (
            <div className="text-xs text-text-subtle">
                No result codes configured. Add codes under Configuration → Clinical → Result Codes.
            </div>
        );
    }

    const scoredCount = scoredByIndex.size;
    const correctCount = Array.from(scoredByIndex.values()).filter((c) => codeByValue.get(c)?.isCorrect).length;

    return (
        <div className="space-y-2">
            {chainingMethod && (
                <div className="text-xs text-text-subtle">
                    Chaining: <span className="font-semibold text-text">{labelForChaining(chainingMethod)}</span>
                </div>
            )}
            <ol className="space-y-1.5">
                {steps.map((step, i) => {
                    const code = scoredByIndex.get(i) ?? null;
                    const codeRow = code ? codeByValue.get(code) ?? null : null;
                    return (
                        <li key={i} className="flex items-center gap-2">
                            <span className="w-5 text-right text-xs font-mono text-text-subtle">{i + 1}.</span>
                            <span className="flex-1 text-sm text-text">{step?.label || `Step ${i + 1}`}</span>
                            <StepPicker
                                code={code}
                                codeRow={codeRow}
                                disabled={disabled}
                                activeCodes={activeCodes}
                                onPick={(c) => setStep(i, c)}
                                onClear={() => setStep(i, null)}
                            />
                        </li>
                    );
                })}
            </ol>
            <div className="text-xs text-text-subtle">
                {scoredCount}/{steps.length} scored · {correctCount} correct ·{' '}
                <span className="font-semibold text-text">
                    {value.valueNumeric === null ? '—' : `${value.valueNumeric}%`}
                </span>
            </div>
        </div>
    );
}

function StepPicker({
    code, codeRow, disabled, activeCodes, onPick, onClear,
}: {
    code: string | null;
    codeRow: TrialResultCode | null;
    disabled?: boolean;
    activeCodes: TrialResultCode[];
    onPick: (code: string) => void;
    onClear: () => void;
}) {
    const hasColor = !!codeRow?.color;
    const isCorrect = codeRow?.isCorrect === true;
    const isIncorrect = codeRow && codeRow.isCorrect === false;
    return (
        <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild disabled={disabled}>
                <button
                    type="button"
                    disabled={disabled}
                    aria-label={codeRow ? `${codeRow.code} — ${codeRow.label}` : 'Score step'}
                    title={codeRow ? `${codeRow.code} — ${codeRow.label}` : 'Score step'}
                    className={cn(
                        'min-w-[3rem] h-7 px-2 rounded-md border text-xs font-bold inline-flex items-center justify-center gap-1',
                        'transition-colors duration-[var(--duration-base)]',
                        'disabled:opacity-50 disabled:cursor-not-allowed',
                        !code && 'bg-background border-border text-text-subtle hover:bg-surface-muted',
                        code && !hasColor && isCorrect   && 'bg-accent border-accent-dark text-text',
                        code && !hasColor && isIncorrect && 'bg-warning-soft border-warning text-warning',
                    )}
                    style={hasColor
                        ? { backgroundColor: codeRow!.color!, color: '#fff', borderColor: codeRow!.color! }
                        : undefined}
                >
                    {code ?? '—'}
                    <ChevronDown className="w-3 h-3 opacity-70" />
                </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    align="end"
                    sideOffset={4}
                    className="min-w-[180px] bg-surface border border-border rounded-md shadow-md py-1 z-[60]"
                >
                    {activeCodes.map((c) => {
                        const hasItemColor = !!c.color;
                        return (
                            <DropdownMenu.Item
                                key={c.id}
                                onSelect={() => onPick(c.code)}
                                className="flex items-center gap-2 px-3 py-1.5 text-sm text-text hover:bg-surface-muted cursor-pointer outline-none"
                            >
                                <span
                                    className={cn(
                                        'min-w-6 px-1.5 text-center rounded text-xs font-bold',
                                        !hasItemColor && c.isCorrect  && 'bg-accent text-text',
                                        !hasItemColor && !c.isCorrect && 'bg-warning-soft text-warning',
                                    )}
                                    style={hasItemColor ? { backgroundColor: c.color!, color: '#fff' } : undefined}
                                >
                                    {c.code}
                                </span>
                                <span className="flex-1">{c.label}</span>
                            </DropdownMenu.Item>
                        );
                    })}
                    {code && (
                        <>
                            <DropdownMenu.Separator className="my-1 h-px bg-border" />
                            <DropdownMenu.Item
                                onSelect={() => onClear()}
                                className="flex items-center gap-2 px-3 py-1.5 text-sm text-text-muted hover:bg-surface-muted cursor-pointer outline-none"
                            >
                                <X className="w-3 h-3" /> Clear
                            </DropdownMenu.Item>
                        </>
                    )}
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
}

function labelForChaining(m: ChainingMethod): string {
    switch (m) {
        case 'total_task': return 'Total task';
        case 'forward':    return 'Forward';
        case 'backward':   return 'Backward';
        default:           return m;
    }
}
